import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Input,
    Textarea,
    Button,
    Select,
    Option,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    Avatar,
    Chip,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/joy';
import PersonIcon from '@mui/icons-material/Person';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HomeIcon from '@mui/icons-material/Home';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import axiosConfig from '../util/axiosConfig';
import { useNavigate } from 'react-router-dom';
import SpecializationSearch from './SpecializationSearch';
import QualificationSearch from './QualificationSearch';

const ORANGE = '#eb7a24';
const ORANGE_DARK = '#d96a14';

const inputSx = {
    '--Input-radius': '8px',
    '--Input-focusedHighlight': ORANGE,
    width: '100%',
    fontSize: '0.875rem',
    bgcolor: '#fff',
};

const selectSx = {
    '--Select-radius': '8px',
    '--Select-focusedHighlight': ORANGE,
    width: '100%',
    fontSize: '0.875rem',
    bgcolor: '#fff',
};

const textareaSx = {
    '--Textarea-radius': '8px',
    '--Textarea-focusedHighlight': ORANGE,
    width: '100%',
    fontSize: '0.875rem',
    bgcolor: '#fff',
};

const Field = ({ label, children, fullWidth, required, error }) => (
    <Box sx={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
        <Typography
            component="label"
            sx={{
                display: 'block',
                color: error ? '#ef4444' : '#374151',
                fontWeight: 600,
                mb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontSize: '0.68rem',
            }}
        >
            {label}
            {required && (
                <Box component="span" sx={{ color: '#ef4444', ml: 0.25 }}>*</Box>
            )}
        </Typography>
        {children}
        {error && (
            <Typography sx={{ color: '#ef4444', fontSize: '0.7rem', mt: 0.5 }}>{error}</Typography>
        )}
    </Box>
);

const SectionCard = ({ title, icon: Icon, children, columns = 3 }) => (
    <Box
        sx={{
            bgcolor: '#fffcfa',
            border: '1px solid #f0dfd0',
            borderRadius: '14px',
            p: { xs: 2, sm: 3 },
            mb: 2.5,
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
                boxShadow: '0 3px 16px rgba(235, 122, 36, 0.09)',
            },
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.25 }}>
            {Icon && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 30,
                        height: 30,
                        bgcolor: 'rgba(235, 122, 36, 0.1)',
                        borderRadius: '8px',
                        color: ORANGE,
                        flexShrink: 0,
                    }}
                >
                    <Icon sx={{ fontSize: '1rem' }} />
                </Box>
            )}
            <Typography
                sx={{
                    color: '#1c1917',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                }}
            >
                {title}
            </Typography>
        </Box>
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gap: 2.5,
            }}
        >
            {children}
        </Box>
    </Box>
);

const SECTION_META = {
    personal:        { icon: PersonIcon },
    contact_info:    { icon: ContactPhoneIcon },
    contact_family:  { icon: PersonIcon },
    contact_address: { icon: HomeIcon },
    ug:              { icon: SchoolIcon },
    pg:              { icon: WorkspacePremiumIcon },
    diploma:         { icon: SchoolIcon },
    xii:             { icon: SchoolIcon },
    x:               { icon: SchoolIcon },
    experience:      { icon: WorkspacePremiumIcon },
};

const CollegeSearch = ({ value, onChange, onSelect }) => {
    const [states, setStates] = useState([]);
    const [selectedState, setSelectedState] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [open, setOpen] = useState(false);
    const timerRef = useRef(null);
    const containerRef = useRef(null);

    // Load state list once on mount
    useEffect(() => {
        axiosConfig.get('/students/colleges/states')
            .then(r => setStates(r.data.states || []))
            .catch(() => {});
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const doSearch = (q, state) => {
        clearTimeout(timerRef.current);
        if (q.trim().length < 2) {
            setResults([]);
            setOpen(false);
            return;
        }
        setSearching(true);
        timerRef.current = setTimeout(async () => {
            try {
                const params = { q };
                if (state) params.state = state;
                const resp = await axiosConfig.get('/students/colleges/search', { params });
                setResults(resp.data.results || []);
                setOpen(true);
            } catch (_) {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 350);
    };

    const handleStateChange = (_, newState) => {
        setSelectedState(newState || '');
        setResults([]);
        setOpen(false);
        // Re-trigger search with new state if there's already a query
        if (value.trim().length >= 2) doSearch(value, newState || '');
    };

    const handleInput = (e) => {
        const q = e.target.value;
        onChange(q);
        doSearch(q, selectedState);
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 1.5, alignItems: 'flex-start' }}>
            {/* State selector */}
            <Select
                placeholder="Filter by state…"
                value={selectedState || null}
                onChange={handleStateChange}
                sx={{ ...selectSx, minWidth: 0 }}
            >
                <Option value="">All states</Option>
                {states.map(s => <Option key={s} value={s}>{s}</Option>)}
            </Select>

            {/* College name search */}
            <Box ref={containerRef} sx={{ position: 'relative' }}>
                <Input
                    value={value}
                    onChange={handleInput}
                    placeholder={selectedState ? `Search college in ${selectedState}…` : 'Type college name to search…'}
                    sx={inputSx}
                    endDecorator={searching ? (
                        <CircularProgress size="sm" sx={{ '--CircularProgress-progressColor': ORANGE }} />
                    ) : null}
                />
                {open && results.length > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 1200,
                            bgcolor: '#fff',
                            border: '1px solid #f0dfd0',
                            borderRadius: '10px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            maxHeight: '260px',
                            overflowY: 'auto',
                        }}
                    >
                        {results.map((c) => (
                            <Box
                                key={c.collegecode}
                                onMouseDown={(e) => { e.preventDefault(); onSelect(c); setOpen(false); }}
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #faf5f0',
                                    '&:hover': { bgcolor: 'rgba(235,122,36,0.06)' },
                                    '&:last-child': { borderBottom: 'none' },
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1c1917', lineHeight: 1.3 }}>
                                    {c.collegename}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#57534e', mt: 0.25 }}>
                                    {[c.universityname, c.state].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const ProfileEdit = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [formData, setFormData] = useState({});
    const [studentInfo, setStudentInfo] = useState({ name: '', studentId: '', batchcode: '' });
    const [photoUrl, setPhotoUrl] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [stateBoards, setStateBoards] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const navigate = useNavigate();

    const TAB_GROUPS = useMemo(() => [
        { id: 'personal',   label: 'Personal',  sectionIds: ['personal'] },
        { id: 'contact',    label: 'Contact',   sectionIds: ['contact_info', 'contact_family', 'contact_address'] },
        { id: 'ug',         label: 'UG',        sectionIds: ['ug'] },
        { id: 'pg',         label: 'PG',        sectionIds: ['pg'] },
        { id: 'academics',  label: 'Academics', sectionIds: ['diploma', 'xii', 'x'] },
        { id: 'experience', label: 'Experience', sectionIds: ['experience'] },
    ], []);

    const FIELD_SECTIONS = useMemo(() => {
        const expOptions = Array.from({ length: 37 }, (_, i) => ({
            value: String(i),
            label: i === 0 ? '0 months (default)' : i === 1 ? '1 month' : `${i} months`,
        }));

        return [
            {
                id: 'personal',
                title: 'Personal Information',
                fields: [
                    {
                        key: 'gender', label: 'Gender', type: 'select', required: true, options: [
                            { value: 'Male',           label: 'Male' },
                            { value: 'Female',         label: 'Female' },
                            { value: 'Prefer not to say', label: 'Prefer not to say' },
                        ]
                    },
                    { key: 'dateofbirth', label: 'Date of Birth', type: 'date', required: true },
                    {
                        key: 'bgroup', label: 'Blood Group', type: 'select', required: true, options: [
                            { value: 'A+',  label: 'A+' },
                            { value: 'A-',  label: 'A-' },
                            { value: 'B+',  label: 'B+' },
                            { value: 'B-',  label: 'B-' },
                            { value: 'O+',  label: 'O+' },
                            { value: 'O-',  label: 'O-' },
                            { value: 'AB+', label: 'AB+' },
                            { value: 'AB-', label: 'AB-' },
                        ],
                    },
                ],
            },
            {
                id: 'contact_info',
                title: 'Contact',
                columns: 3,
                fields: [
                    { key: 'mobileno',  label: 'Mobile',    type: 'text',  required: true },
                    { key: 'email',     label: 'Email',     type: 'email', required: true },
                    { key: 'landline',  label: 'Landline',  type: 'text' },
                ],
            },
            {
                id: 'contact_family',
                title: 'Family',
                columns: 2,
                fields: [
                    { key: 'fathername',    label: "Father's Name",    type: 'text', required: true },
                    { key: 'fathercontact', label: "Father's Contact", type: 'text', required: true },
                ],
            },
            {
                id: 'contact_address',
                title: 'Address',
                columns: 2,
                fields: [
                    { key: 'city',  label: 'City',  type: 'text' },
                    { key: 'state', label: 'State', type: 'state-select' },
                    { key: 'address',    label: 'Permanent Address', type: 'textarea', rows: 3, fullWidth: true },
                    { key: 'locaddress', label: 'Local Address',     type: 'textarea', rows: 3, fullWidth: true },
                ],
            },
            {
                id: 'ug',
                title: 'Under Graduation (UG)',
                columns: 3,
                fields: [
                    { key: 'qualification',    label: 'Qualification',         type: 'qualification', mode: 'ug', required: true },
                    { key: 'ugspecialization', label: 'Specialization',        type: 'specialization', required: true },
                    { key: 'ugyop',            label: 'Year of Passout',       type: 'year',   required: true },
                    { key: 'backlog',          label: 'Current Backlogs',      type: 'number',     required: true, max: 99 },
                    { key: 'tbacklog',         label: 'History of Backlogs',   type: 'number',     required: true, max: 99 },
                    { key: 'ugcertper',        label: 'Certificate %',         type: 'percentage', required: true, cgpaAllowed: true },
                    { key: 'ugaggrper',        label: 'Aggregate %',           type: 'percentage', required: true, cgpaAllowed: true },
                    { key: 'project',          label: 'Final Project',         type: 'text',   required: true, fullWidth: true },
                    { key: 'ugcollege',        label: 'College Name',          type: 'college-search', mode: 'ug', required: true, fullWidth: true },
                    { key: 'ugboard',          label: 'University',            type: 'text',   required: true, readonly: true },
                    { key: 'ugcity',           label: 'City',                  type: 'text',   required: true },
                    { key: 'ugstate',          label: 'State',                 type: 'text',   required: true, readonly: true },
                    { key: 'ugcollegeaddr',    label: 'College Address',       type: 'textarea', rows: 2, required: true, fullWidth: true, readonly: true },
                    { key: 'ugcollegesite',    label: 'College Website',       type: 'text',   required: true, readonly: true },
                ],
            },
            {
                id: 'pg',
                title: 'Post Graduation (PG)',
                columns: 3,
                fields: [
                    { key: 'pgqualification', label: 'Qualification',         type: 'qualification', mode: 'pg' },
                    { key: 'specialization',  label: 'Specialization',        type: 'specialization' },
                    { key: 'pgyop',           label: 'Year of Passout',       type: 'year' },
                    { key: 'pgbacklog',       label: 'Current Backlogs',      type: 'number',     max: 99 },
                    { key: 'tpgbacklog',      label: 'History of Backlogs',   type: 'number',     max: 99 },
                    { key: 'pgcertper',       label: 'Certificate %',         type: 'percentage', cgpaAllowed: true },
                    { key: 'pgaggrper',       label: 'Aggregate %',           type: 'percentage', cgpaAllowed: true },
                    { key: 'pgproject',       label: 'Final Project',         type: 'text',   fullWidth: true },
                    { key: 'college',         label: 'College Name',          type: 'college-search', mode: 'pg', fullWidth: true },
                    { key: 'university',      label: 'University',            type: 'text',   readonly: true },
                    { key: 'ccity',           label: 'City',                  type: 'text' },
                    { key: 'cstate',          label: 'State',                 type: 'text',   readonly: true },
                    { key: 'collegeaddress',  label: 'College Address',       type: 'textarea', rows: 2, fullWidth: true, readonly: true },
                    { key: 'collegesite',     label: 'College Website',       type: 'text',   readonly: true },
                ],
            },
            {
                id: 'diploma',
                title: 'Diploma',
                fields: [
                    { key: '_diplomastate',     label: 'State',                   type: 'board-state', boardKey: 'diplomaboard', boardType: 'diploma', fullWidth: true },
                    { key: 'diplomapercentage', label: 'Diploma Percentage',      type: 'percentage' },
                    { key: 'diplomaboard',      label: 'Diploma Board',           type: 'text', readonly: true },
                    { key: 'diplomayop',        label: 'Diploma Year of Passing', type: 'year' },
                ],
            },
            {
                id: 'xii',
                title: 'Intermediate (12th)',
                fields: [
                    { key: '_xiistate',       label: 'State',                   type: 'board-state', boardKey: 'xiiboard', boardType: 'inter', fullWidth: true },
                    { key: 'interpercentage', label: 'Inter Percentage',         type: 'percentage' },
                    { key: 'xiiboard',        label: 'Inter Board',             type: 'text', readonly: true },
                    { key: 'xiiyop',          label: 'Inter Year of Passing',   type: 'year' },
                ],
            },
            {
                id: 'x',
                title: '10th Class',
                fields: [
                    { key: '_xstate',          label: 'State',                   type: 'board-state', boardKey: 'xboard', boardType: 'ssc', fullWidth: true },
                    { key: 'tenthpercentage',  label: 'Tenth Percentage',        type: 'percentage', required: true },
                    { key: 'xboard',           label: 'Tenth Board',             type: 'text',   required: true, readonly: true },
                    { key: 'xyop',             label: 'Tenth Year of Passing',   type: 'year',   required: true },
                ],
            },
            {
                id: 'experience',
                title: 'Experience',
                columns: 2,
                fields: [
                    { key: 'expmonth',   label: 'Experience',         type: 'select', options: expOptions },
                    { key: 'experience', label: 'Experience Remarks', type: 'text' },
                ],
            },
        ];
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                setError(null);
                const [profileResp, permResp, boardsResp] = await Promise.all([
                    axiosConfig.get('/students/profile'),
                    axiosConfig.get('/students/profile/edit-permission'),
                    axiosConfig.get('/students/stateboards').catch(() => ({ data: { stateboards: [] } })),
                ]);
                setStateBoards(boardsResp.data?.stateboards || []);
                setCanEdit(Boolean(permResp.data?.canEdit));
                const p = profileResp.data || {};
                setStudentInfo({
                    name:      p.name || '',
                    studentId: p.studentId || p.studentid || '',
                    batchcode: p.batchcode || '',
                });
                setPhotoUrl(p.photoUrl || null);
                setFormData({
                    gender:       p.gender || '',
                    bgroup:       p.bgroup || '',
                    dateofbirth:  toInputDate(p.dateofbirth) || '',
                    mobileno:      p.mobileno || '',
                    email:         p.email || '',
                    landline:      p.landline || '',
                    fathername:    p.fathername || '',
                    fathercontact: p.fathercontact || '',
                    address:       p.address || '',
                    city:          p.city || '',
                    state:         p.state || '',
                    locaddress:    p.laddress || '',
                    qualification:   p.qualification || '',
                    project:         p.project || '',
                    backlog:         p.backlog || '0',
                    ugcertper:       p.ugcertper ? String(p.ugcertper) : '',
                    ugaggrper:       p.ugaggrper ? String(p.ugaggrper) : '',
                    ugspecialization: p.ugspec || '',
                    ugboard:         p.ugboard || '',
                    ugcollege:       p.ugcollege || '',
                    ugcollegeaddr:   p.ugcolladdr || '',
                    ugcollegesite:   p.ugsite || '',
                    ugcity:          p.ugcity || '',
                    ugstate:         p.ugstate || '',
                    ugyop:           p.ugyop || '',
                    ugcollegecode:   p.ugcollegecode || '',
                    tbacklog:        p.tugbacklog || '0',
                    pgqualification: p.pgqualification || '',
                    pgcertper:      p.pgcertper ? String(p.pgcertper) : '',
                    pgaggrper:      p.pgaggrper ? String(p.pgaggrper) : '',
                    pgbacklog:      p.pgbacklog || '0',
                    pgproject:      p.pgproject || '',
                    specialization: p.pgspec || '',
                    university:     p.pgboard || '',
                    college:        p.pgcollege || '',
                    collegeaddress: p.pgcolladdr || '',
                    collegesite:    p.pgsite || '',
                    ccity:          p.pgcity || '',
                    cstate:         p.pgstate || '',
                    pgyop:          p.pgyop || '',
                    tpgbacklog:     p.tpgbacklog || '0',
                    pgcollegecode:  p.pgcollegecode || '',
                    diplomapercentage: p.diplomapercentage || '',
                    diplomaboard:      p.diplomaboard || '',
                    diplomayop:        p.diplomayop || '',
                    interpercentage:   p.interpercentage || '',
                    xiiboard:          p.xiiboard || '',
                    xiiyop:            p.xiiyop || '',
                    tenthpercentage:   p.tenthpercentage || '',
                    xboard:            p.xboard || '',
                    xyop:              p.xyop || '',
                    _diplomastate:     '',
                    _xiistate:         '',
                    _xstate:           '',
                    experience: p.expe || '',
                    expmonth:   p.expmonth || '0',
                });
            } catch (e) {
                setError(e.response?.data?.errorMessage || 'Failed to load profile');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const CERT_TO_AGGR = { ugcertper: 'ugaggrper', pgcertper: 'pgaggrper' };
    const CGPA_KEYS = new Set(['ugcertper', 'ugaggrper', 'pgcertper', 'pgaggrper']);

    const cgpaPercentageError = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const val = parseFloat(value);
        if (isNaN(val)) return 'Enter a valid number';
        if (val === 0) return null;
        const isCgpa = val >= 1 && val <= 10;
        const isPct  = val >= 50 && val <= 100;
        if (!isCgpa && !isPct) return 'Enter CGPA (1–10) or percentage (≥ 50)';
        return null;
    };

    const onChange = (key) => (e) => {
        const value = e?.target?.value ?? '';
        const aggrKey = CERT_TO_AGGR[key];
        setFormData(prev => aggrKey
            ? { ...prev, [key]: value, [aggrKey]: value }
            : { ...prev, [key]: value }
        );
        setFieldErrors(prev => {
            const next = { ...prev };
            if (CGPA_KEYS.has(key)) {
                const err = cgpaPercentageError(value);
                const affected = aggrKey ? [key, aggrKey] : [key];
                affected.forEach(k => { if (err) next[k] = err; else delete next[k]; });
            } else {
                delete next[key];
            }
            return next;
        });
    };

    const validateForm = () => {
        const errors = {};
        FIELD_SECTIONS.forEach(section => {
            section.fields.forEach(f => {
                const raw = formData[f.key];
                if (raw === '' || raw === null || raw === undefined) return;
                if (f.type === 'percentage') {
                    const val = parseFloat(raw);
                    if (isNaN(val)) {
                        errors[f.key] = 'Enter a valid number';
                    } else if (val !== 0) {
                        if (f.cgpaAllowed) {
                            const isCgpa = val >= 1 && val <= 10;
                            const isPct  = val >= 50 && val <= 100;
                            if (!isCgpa && !isPct)
                                errors[f.key] = 'Enter CGPA (1–10) or percentage (≥ 50)';
                        } else if (val <= 10 || val > 100) {
                            errors[f.key] = 'Must be > 10 and ≤ 100 (or 0)';
                        }
                    }
                }
                if (f.type === 'number' && f.max !== undefined) {
                    const val = parseInt(raw, 10);
                    if (isNaN(val) || val < 0) {
                        errors[f.key] = 'Cannot be negative';
                    } else if (val >= 100) {
                        errors[f.key] = 'Must be less than 100';
                    }
                }
                if (f.type === 'year') {
                    const val = parseInt(raw, 10);
                    if (!isNaN(val) && (val < 1900 || val > new Date().getFullYear() + 5)) {
                        errors[f.key] = 'Enter a valid 4-digit year';
                    }
                }
            });
        });
        return errors;
    };

    const onSave = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError('Please fix the highlighted fields before saving.');
            return;
        }
        setFieldErrors({});
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await axiosConfig.put('/students/profile', formData);
            setSuccess('Profile updated successfully.');
            setTimeout(() => navigate('/profile'), 600);
        } catch (e) {
            setError(e.response?.data?.errorMessage || 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const getSelectValue = (field, currentVal) => {
        const opts = field.options || [];
        const cur = (currentVal ?? '').toString();
        if (cur === '') return '';
        const found = opts.find(opt => {
            const v = (opt && typeof opt === 'object') ? String(opt.value) : String(opt);
            return v.toLowerCase() === cur.toLowerCase();
        });
        if (!found) return cur;
        return (found && typeof found === 'object') ? found.value : found;
    };

    const toInputDate = (val) => {
        if (!val) return '';
        const s = String(val);
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
        const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m) {
            const [, d, mo, y] = m;
            return `${y}-${mo}-${d}`;
        }
        return '';
    };

    const YEAR_OPTIONS = useMemo(() => {
        const now = new Date().getFullYear();
        const list = [];
        for (let y = now + 3; y >= now - 10; y--) {
            list.push({ value: String(y), label: String(y) });
        }
        return list;
    }, []);

    const earlyYearOptions = useMemo(() => {
        const now = new Date().getFullYear();
        const ugYop = parseInt(formData.ugyop, 10);
        const minYear = !isNaN(ugYop) ? Math.min(now - 10, ugYop - 7) : now - 20;
        const list = [];
        for (let y = now + 3; y >= minYear; y--) {
            list.push({ value: String(y), label: String(y) });
        }
        return list;
    }, [formData.ugyop]);

    const YEAR_FIELD_KEYS = useMemo(() => ['xyop', 'xiiyop', 'diplomayop', 'ugyop', 'pgyop'], []);
    const EARLY_YEAR_FIELD_KEYS = useMemo(() => new Set(['xyop', 'xiiyop', 'diplomayop']), []);
    const [customYearKeys, setCustomYearKeys] = useState({});

    useEffect(() => {
        let changed = false;
        const updates = {};
        for (const key of YEAR_FIELD_KEYS) {
            const opts = EARLY_YEAR_FIELD_KEYS.has(key) ? earlyYearOptions : YEAR_OPTIONS;
            const raw = formData[key];
            const normalized = getSelectValue({ options: opts }, raw) || '';
            const shouldBeCustom = (raw ?? '') !== '' && normalized === '';
            const currentFlag = customYearKeys[key] === true;
            // Only auto-enable custom mode (e.g. on initial load when stored value is outside dropdown range).
            // Never auto-disable: switching back to select is handled by the Select's own onChange.
            if (shouldBeCustom && !currentFlag) { updates[key] = true; changed = true; }
        }
        if (changed) setCustomYearKeys(prev => ({ ...prev, ...updates }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.xyop, formData.xiiyop, formData.diplomayop, formData.ugyop, formData.pgyop, YEAR_OPTIONS, earlyYearOptions]);

    // PG: required when any substantive PG field has a value
    const isPgRequired = useMemo(() => {
        const triggers = ['pgqualification', 'pgcertper', 'pgaggrper', 'university', 'college', 'pgyop', 'specialization', 'pgproject'];
        return triggers.some(k => String(formData[k] ?? '').trim() !== '');
    }, [formData]);

    // Diploma: required when YOP is entered
    const isDiplomaRequired = useMemo(() =>
        Boolean(String(formData.diplomayop ?? '').trim()),
    [formData.diplomayop]);

    // Inter: required when YOP is entered
    const isInterRequired = useMemo(() =>
        Boolean(String(formData.xiiyop ?? '').trim()),
    [formData.xiiyop]);

    const getFieldRequired = (sectionId, field) => {
        if (field.required) return true;
        if (sectionId === 'pg') return isPgRequired;
        if (sectionId === 'diploma') return isDiplomaRequired;
        if (sectionId === 'xii') return isInterRequired;
        return false;
    };

    const readonlyInputSx = {
        ...inputSx,
        bgcolor: '#f5f5f4',
        '--Input-focusedHighlight': 'transparent',
        pointerEvents: 'none',
        userSelect: 'text',
    };

    const readonlyTextareaSx = {
        ...textareaSx,
        bgcolor: '#f5f5f4',
        '--Textarea-focusedHighlight': 'transparent',
        pointerEvents: 'none',
        userSelect: 'text',
    };

    const renderFieldInput = (f) => {
        if (f.type === 'textarea') {
            return (
                <Textarea
                    minRows={f.rows || 2}
                    value={formData[f.key] || ''}
                    onChange={f.readonly ? undefined : onChange(f.key)}
                    sx={f.readonly ? readonlyTextareaSx : textareaSx}
                    slotProps={f.readonly ? { textarea: { readOnly: true } } : undefined}
                />
            );
        }
        if (f.type === 'select') {
            const normalized = getSelectValue({ options: f.options }, formData[f.key]) || '';
            return (
                <Select
                    value={normalized}
                    onChange={(e, val) => setFormData(prev => ((prev[f.key] || '') === (val || '') ? prev : { ...prev, [f.key]: val || '' }))}
                    sx={selectSx}
                    placeholder={f.placeholder || 'Select'}
                >
                    {(f.options || []).map(opt => {
                        const val = (opt && typeof opt === 'object') ? opt.value : opt;
                        const text = (opt && typeof opt === 'object') ? (opt.label ?? opt.value) : opt;
                        return <Option key={String(val)} value={val}>{text}</Option>;
                    })}
                </Select>
            );
        }
        if (f.type === 'date') {
            return (
                <Input
                    type="date"
                    value={toInputDate(formData[f.key])}
                    onChange={(e) => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                    sx={inputSx}
                />
            );
        }
        if (f.type === 'year') {
            const opts = EARLY_YEAR_FIELD_KEYS.has(f.key) ? earlyYearOptions : YEAR_OPTIONS;
            const raw = formData[f.key];
            const normalized = getSelectValue({ options: opts }, raw) || '';
            const isCustom = (customYearKeys[f.key] === true) || ((raw ?? '') !== '' && normalized === '');
            const maxYear = new Date().getFullYear() + 3;
            return (
                <Box>
                    <Select
                        value={isCustom ? '__custom__' : normalized}
                        onChange={(e, val) => {
                            if (val === '__custom__') {
                                setCustomYearKeys(prev => (prev[f.key] === true ? prev : { ...prev, [f.key]: true }));
                            } else {
                                setCustomYearKeys(prev => (prev[f.key] === false || typeof prev[f.key] === 'undefined' ? prev : { ...prev, [f.key]: false }));
                                setFormData(prev => ((prev[f.key] || '') === (val || '') ? prev : { ...prev, [f.key]: val || '' }));
                            }
                        }}
                        sx={selectSx}
                        placeholder="Select Year"
                    >
                        {opts.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
                        <Option value="__custom__">Custom year…</Option>
                    </Select>
                    {isCustom && (
                        <Input
                            type="number"
                            placeholder="Enter 4-digit year (e.g. 2018)"
                            value={formData[f.key] || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                            slotProps={{ input: { min: 1900, max: maxYear } }}
                            sx={{ ...inputSx, mt: 1 }}
                        />
                    )}
                </Box>
            );
        }
        if (f.type === 'percentage') {
            const hasErr = Boolean(fieldErrors[f.key]);
            return (
                <Input
                    type="number"
                    value={formData[f.key] ?? ''}
                    onChange={onChange(f.key)}
                    placeholder="0"
                    slotProps={{ input: { min: 0, max: 100, step: 0.01 } }}
                    sx={hasErr ? { ...inputSx, '--Input-focusedHighlight': '#ef4444', boxShadow: 'inset 0 0 0 1.5px #ef4444' } : inputSx}
                />
            );
        }
        if (f.type === 'number') {
            const hasErr = Boolean(fieldErrors[f.key]);
            return (
                <Input
                    type="number"
                    value={formData[f.key] ?? ''}
                    onChange={onChange(f.key)}
                    placeholder={f.max !== undefined ? '0' : f.placeholder}
                    slotProps={f.max !== undefined ? { input: { min: 0, max: f.max } } : undefined}
                    sx={hasErr ? { ...inputSx, '--Input-focusedHighlight': '#ef4444', boxShadow: 'inset 0 0 0 1.5px #ef4444' } : inputSx}
                />
            );
        }
        if (f.type === 'email') {
            return (
                <Input
                    type="email"
                    value={formData[f.key] || ''}
                    onChange={onChange(f.key)}
                    placeholder={f.placeholder}
                    sx={inputSx}
                />
            );
        }
        if (f.type === 'board-state') {
            return (
                <Select
                    placeholder="Select state to auto-fill board…"
                    value={formData[f.key] || null}
                    onChange={(_, newState) => {
                        const row = stateBoards.find(r => r.state === newState);
                        const boardName = row?.[f.boardType] || '';
                        setFormData(prev => ({
                            ...prev,
                            [f.key]: newState || '',
                            [f.boardKey]: boardName,
                        }));
                    }}
                    sx={selectSx}
                >
                    {stateBoards.map(r => (
                        <Option key={r.state} value={r.state}>{r.state}</Option>
                    ))}
                </Select>
            );
        }
        if (f.type === 'state-select') {
            return (
                <Select
                    value={formData[f.key] || null}
                    onChange={(_, val) => setFormData(prev => ({ ...prev, [f.key]: val || '' }))}
                    sx={selectSx}
                    placeholder="Select state"
                >
                    {stateBoards.map(r => (
                        <Option key={r.state} value={r.state}>{r.state}</Option>
                    ))}
                </Select>
            );
        }
        if (f.type === 'specialization') {
            return (
                <SpecializationSearch
                    value={formData[f.key] || ''}
                    onChange={(val) => {
                        setFormData(prev => ({ ...prev, [f.key]: val }));
                        setFieldErrors(prev => { const next = { ...prev }; delete next[f.key]; return next; });
                    }}
                    required={f.required}
                    error={Boolean(fieldErrors[f.key])}
                />
            );
        }
        if (f.type === 'qualification') {
            return (
                <QualificationSearch
                    value={formData[f.key] || ''}
                    onChange={(val) => {
                        setFormData(prev => ({ ...prev, [f.key]: val }));
                        setFieldErrors(prev => { const next = { ...prev }; delete next[f.key]; return next; });
                    }}
                    mode={f.mode}
                    required={f.required}
                    error={Boolean(fieldErrors[f.key])}
                />
            );
        }
        if (f.type === 'college-search') {
            return (
                <CollegeSearch
                    value={formData[f.key] || ''}
                    onChange={(text) => setFormData(prev => ({ ...prev, [f.key]: text }))}
                    onSelect={(college) => {
                        if (f.mode === 'ug') {
                            setFormData(prev => ({
                                ...prev,
                                ugcollege:     college.collegename    || '',
                                ugboard:       college.universityname || '',
                                ugcollegeaddr: college.collegeaddress || '',
                                ugstate:       college.state          || '',
                                ugcollegesite: college.website        || '',
                                ugcollegecode: college.collegecode    || '',
                            }));
                        } else {
                            setFormData(prev => ({
                                ...prev,
                                college:        college.collegename    || '',
                                university:     college.universityname || '',
                                collegeaddress: college.collegeaddress || '',
                                cstate:         college.state          || '',
                                collegesite:    college.website        || '',
                                pgcollegecode:  college.collegecode    || '',
                            }));
                        }
                    }}
                />
            );
        }
        return (
            <Input
                type="text"
                value={formData[f.key] || ''}
                onChange={f.readonly ? undefined : onChange(f.key)}
                placeholder={f.placeholder}
                sx={f.readonly ? readonlyInputSx : inputSx}
                slotProps={f.readonly ? { input: { readOnly: true } } : undefined}
            />
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size="lg" sx={{ '--CircularProgress-trackColor': 'rgba(235,122,36,0.15)', '--CircularProgress-progressColor': ORANGE }} />
                    <Typography level="body-sm" sx={{ color: '#57534e', mt: 2 }}>Loading your profile…</Typography>
                </Box>
            </Box>
        );
    }

    if (!canEdit) {
        return (
            <Box sx={{ p: 0 }}>
                <Box
                    sx={{
                        bgcolor: '#fffcfa',
                        border: '1px solid #f0dfd0',
                        borderRadius: '14px',
                        p: 3,
                        mb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Alert
                        color="warning"
                        variant="soft"
                        sx={{ borderRadius: '10px' }}
                    >
                        Editing is currently locked. Please request access from the Support section.
                    </Alert>
                    <Button
                        variant="solid"
                        onClick={() => navigate('/support')}
                        sx={{
                            alignSelf: 'flex-start',
                            bgcolor: ORANGE,
                            borderRadius: '8px',
                            px: 3,
                            fontWeight: 600,
                            '&:hover': { bgcolor: ORANGE_DARK },
                        }}
                    >
                        Go to Support
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 0, m: 0, width: '100%' }}>
            {/* Top bar — shown on all tabs */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: '#fffcfa',
                    border: '1px solid #f0dfd0',
                    borderRadius: '14px',
                    p: { xs: 2, sm: 2.5 },
                    mb: 3,
                }}
            >
                <Avatar
                    src={photoUrl || undefined}
                    sx={{
                        width: 64,
                        height: 64,
                        flexShrink: 0,
                        border: `2px solid ${ORANGE}`,
                        bgcolor: 'rgba(235,122,36,0.1)',
                        color: ORANGE,
                        fontSize: '1.5rem',
                        fontWeight: 700,
                    }}
                >
                    {!photoUrl && (studentInfo.name?.[0] || '?')}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: '1.1rem',
                            color: '#1c1917',
                            letterSpacing: '-0.01em',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {studentInfo.name || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                        {studentInfo.studentId && (
                            <Chip
                                size="sm"
                                variant="soft"
                                sx={{ bgcolor: 'rgba(235,122,36,0.1)', color: ORANGE, fontWeight: 600, fontSize: '0.75rem' }}
                            >
                                {studentInfo.studentId}
                            </Chip>
                        )}
                        {studentInfo.batchcode && (
                            <Chip
                                size="sm"
                                variant="soft"
                                sx={{ bgcolor: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: '0.75rem' }}
                            >
                                Batch: {studentInfo.batchcode}
                            </Chip>
                        )}
                    </Box>
                </Box>
                <Typography
                    sx={{
                        color: ORANGE,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        alignSelf: 'flex-start',
                        flexShrink: 0,
                    }}
                >
                    Edit Profile
                </Typography>
            </Box>

            {error   && <Alert color="danger"  variant="soft" sx={{ mb: 2.5, borderRadius: '10px' }}>{error}</Alert>}
            {success && <Alert color="success" variant="soft" sx={{ mb: 2.5, borderRadius: '10px' }}>{success}</Alert>}

            <Tabs
                value={activeTab}
                onChange={(_, val) => setActiveTab(val)}
                sx={{ bgcolor: 'transparent' }}
            >
                {/* Pill-style tab bar */}
                <TabList
                    sx={{
                        flexWrap: 'wrap',
                        bgcolor: '#f9f3ee',
                        border: '1px solid #ecddd0',
                        borderRadius: '12px',
                        p: '4px',
                        gap: '4px',
                        mb: 3,
                        '--Tab-indicatorThickness': '0px',
                        '& button': {
                            borderRadius: '8px',
                            fontWeight: 500,
                            fontSize: '0.83rem',
                            py: 0.875,
                            px: 2,
                            color: '#44403c',
                            minHeight: '34px',
                            transition: 'all 0.15s ease',
                            letterSpacing: '0.01em',
                        },
                        '& button[aria-selected="true"]': {
                            bgcolor: ORANGE,
                            color: '#fff',
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(235, 122, 36, 0.38)',
                        },
                        '& button[aria-selected="false"]:hover': {
                            bgcolor: 'rgba(235, 122, 36, 0.1)',
                            color: ORANGE,
                        },
                    }}
                >
                    {TAB_GROUPS.map((tab) => (
                        <Tab key={tab.id}>{tab.label}</Tab>
                    ))}
                </TabList>

                {/* Tab panels */}
                {TAB_GROUPS.map((tab, i) => (
                    <TabPanel key={tab.id} value={i} sx={{ p: 0 }}>
                        {FIELD_SECTIONS
                            .filter(s => tab.sectionIds.includes(s.id))
                            .map((section) => (
                                <SectionCard
                                    key={section.id}
                                    title={section.title}
                                    icon={SECTION_META[section.id]?.icon}
                                    columns={section.columns || 3}
                                >
                                    {section.fields.map(f => (
                                        <Field
                                            key={f.key}
                                            label={f.label}
                                            fullWidth={f.fullWidth}
                                            required={getFieldRequired(section.id, f)}
                                            error={fieldErrors[f.key]}
                                        >
                                            {renderFieldInput(f)}
                                        </Field>
                                    ))}
                                </SectionCard>
                            ))
                        }
                    </TabPanel>
                ))}
            </Tabs>

            {/* Footer action bar */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1.5,
                    pt: 2.5,
                    mt: 0.5,
                    borderTop: '1px solid #f0e4d8',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={() => navigate('/profile')}
                    sx={{
                        borderColor: '#d6ccc4',
                        color: '#44403c',
                        borderRadius: '8px',
                        px: 3,
                        fontWeight: 500,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            borderColor: ORANGE,
                            color: ORANGE,
                            bgcolor: 'rgba(235, 122, 36, 0.05)',
                        },
                    }}
                >
                    Cancel
                </Button>
                {activeTab < TAB_GROUPS.length - 1 ? (
                    <Button
                        onClick={() => setActiveTab(prev => prev + 1)}
                        sx={{
                            bgcolor: ORANGE,
                            color: '#fff',
                            borderRadius: '8px',
                            px: 4,
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(235, 122, 36, 0.35)',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                bgcolor: ORANGE_DARK,
                                boxShadow: '0 4px 14px rgba(235, 122, 36, 0.45)',
                                transform: 'translateY(-1px)',
                            },
                            '&:active': { transform: 'translateY(0)' },
                        }}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        onClick={() => setConfirmOpen(true)}
                        sx={{
                            bgcolor: ORANGE,
                            color: '#fff',
                            borderRadius: '8px',
                            px: 4,
                            fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(235, 122, 36, 0.35)',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                bgcolor: ORANGE_DARK,
                                boxShadow: '0 4px 14px rgba(235, 122, 36, 0.45)',
                                transform: 'translateY(-1px)',
                            },
                            '&:active': { transform: 'translateY(0)' },
                        }}
                    >
                        Save
                    </Button>
                )}
            </Box>

            {/* Confirmation dialog */}
            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 420 }}>
                    <DialogTitle>
                        <WarningRoundedIcon sx={{ color: ORANGE, mr: 1 }} />
                        Confirm Submission
                    </DialogTitle>
                    <DialogContent>
                        <Typography level="body-md" sx={{ mb: 1 }}>
                            Are you sure you want to submit your profile updates?
                        </Typography>
                        <Typography level="body-sm" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                            Once submitted, you will not be able to change these values.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="solid"
                            loading={saving}
                            onClick={async () => { setConfirmOpen(false); await onSave(); }}
                            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: ORANGE_DARK }, color: '#fff' }}
                        >
                            Yes, Submit
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            disabled={saving}
                            onClick={() => setConfirmOpen(false)}
                        >
                            No
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default ProfileEdit;
